// Adapted from web-app/libs/nextjs/sentry-config.js (no @twisto/environment).

const IS_DEV = process.env.NODE_ENV === 'development'

/**
 * The one Sentry project this repo reports to. `customer` and `flow` are two
 * builds of a single product, not two products, so they share a project and
 * are told apart by the `app` tag set in createSentryClientConfig below.
 *
 * All three values must match the same project in Sentry: SENTRY_DSN is where
 * the SDK sends events, SENTRY_ORG/SENTRY_PROJECT are where the build uploads
 * source maps. If they drift, stack traces stay minified.
 *
 * The DSN is a public value — it ships inside the client bundle and is
 * readable in devtools — so it travels as a plain build arg rather than a
 * secret. It is read at build time only: `next build` bakes it into the
 * bundles, so setting it on the running container changes nothing.
 *
 * Two things stop an unset value from reproducing DO-2490, where the SDK
 * initialised and then dropped every event with no visible symptom:
 * assertSentryConfigured() in create-b2b-next-config.js fails the build when a
 * deployed WEB_ENV has no DSN, and that same file lists SENTRY_DSN under
 * `env:`, which is what carries the value into the browser bundle. Drop that
 * entry and the server would report while the browser stayed silent, with a
 * green build either way.
 */
const SENTRY_ORG = 'twisto'
const SENTRY_PROJECT = process.env.SENTRY_PROJECT ?? ''
const SENTRY_DSN = process.env.SENTRY_DSN ?? ''
exports.SENTRY_ORG = SENTRY_ORG
exports.SENTRY_PROJECT = SENTRY_PROJECT
exports.SENTRY_DSN = SENTRY_DSN

/** @type {Partial<import('@sentry/nextjs/types/index.server').SentryWebpackPluginOptions>} */
const defaultSentryPluginConfig = {
  silent: true,
  release: process.env.GIT_COMMIT,
  authToken: process.env.SENTRY_AUTH_TOKEN,
  tracesSampleRate: 0,
  url: 'https://sentry.io/',
  org: SENTRY_ORG,
  project: SENTRY_PROJECT,
}
exports.defaultSentryPluginConfig = defaultSentryPluginConfig

const DEFAULT_CLIENT_SAMPLE_RATE = 0.02

const isTestServer =
  typeof window !== 'undefined' &&
  window.location.origin.includes('.ts.twisto.wtf')

const sentryEnvironment = isTestServer
  ? 'testserver'
  : process.env.WEB_ENV ?? 'development'

/** @type {import('@sentry/react').BrowserOptions} */
const baseSentryClientConfig = {
  environment: sentryEnvironment,
  tracesSampleRate: DEFAULT_CLIENT_SAMPLE_RATE,
  tracesSampler: (samplingContext) => {
    const { transactionContext } = samplingContext
    if (transactionContext.op === 'middleware.nextjs') {
      return 0
    }

    return DEFAULT_CLIENT_SAMPLE_RATE
  },
  enabled: !IS_DEV,
  beforeSend: (event) => {
    const { request } = event
    if (request?.headers?.['User-Agent'].includes('jsdom') === true) {
      return null
    }

    return event
  },
  ignoreErrors: [
    'ResizeObserver loop completed with undelivered notifications.',
    'Object Not Found Matching Id:',
    'Failed to fetch',
    'Load failed',
    'NetworkError when attempting to fetch resource.',
    'The operation was aborted',
    'Fetch is aborted',
    'The user aborted a request',
    'top.GLOBALS',
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    "Can't find variable: dat",
    'dat is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    'fb_xd_fragment',
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    'conduitPage',
  ],
  denyUrls: [
    /graph\.facebook\.com/i,
    /connect\.facebook\.net\/en_US\/all\.js/i,
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    /extensions\//i,
    /^chrome:\/\//i,
    /^chrome-extension:\/\//i,
    /127\.0\.0\.1:4001\/isrunning/i,
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
    /\/(ga|analytics|clarity)\.js/i,
  ],
}

/**
 * Browser config for one app. The `app` tag is what separates customer from
 * flow inside the shared project — search `app:flow` in Sentry to get one
 * app's issues back.
 *
 * @param {'customer' | 'flow'} app
 * @returns {import('@sentry/react').BrowserOptions}
 */
function createSentryClientConfig(app) {
  return {
    ...baseSentryClientConfig,
    dsn: SENTRY_DSN,
    initialScope: { tags: { app } },
  }
}
exports.createSentryClientConfig = createSentryClientConfig

/**
 * Server and edge runtimes report uncaught errors only — no traces, and none
 * of the browser-specific noise filtering above applies there.
 *
 * @param {'customer' | 'flow'} app
 * @returns {import('@sentry/nextjs').NodeOptions}
 */
function createSentryServerConfig(app) {
  return {
    dsn: SENTRY_DSN,
    environment: sentryEnvironment,
    tracesSampleRate: 0,
    enabled: !IS_DEV,
    initialScope: { tags: { app } },
  }
}
exports.createSentryServerConfig = createSentryServerConfig
